import { IBarAccidental, IBeaming, INote, ISettings, ISignature } from "../../../../../common/types";

interface IProps {
    notes: INote[],
    signature: ISignature,
    pitchPositions: (null | number[])[],
    onlyGroup?: boolean
};

export interface IGroup {
    note: INote,
    index: number,
};

export const parseBeams = ({ notes, signature, pitchPositions, onlyGroup }: IProps) => {
    let unit = 1;
    if (
        signature &&
        ((signature.num === 3 && signature.den === 8) ||
            (signature.num === 5 && signature.den === 8) ||
            (signature.num === 6 && signature.den === 8) ||
            (signature.num === 7 && signature.den === 8) ||
            (signature.num === 9 && signature.den === 8))
    ) {
        unit = 1.5;
    }
    if (
        signature &&
        ((signature.num === 2 && signature.den === 2) ||
            (signature.num === 3 && signature.den === 2) ||
            (signature.num === 4 && signature.den === 2))
    ) {
        unit = 2;
    }

    if (signature.beam2) {
        unit = 2;
    }

    const beaming: IBeaming[] = [];
    const groups: IGroup[][] = [[]];
    let timePoint = 0;
    let done = 0;
    if (signature.upbeat && signature.display === undefined) {
        const totalBarLength = (4 / signature.den) * signature.num * 3;
        timePoint = totalBarLength - signature.upbeat * 3;
        while (done < timePoint) {
            done += unit * 3;
        }
        done -= unit * 3;
    }
    notes.forEach((note, index) => {
        while (timePoint - done >= unit * 3) {
            groups.push([]);
            done += unit * 3;
        }
        if (
            done === 1.5 * 3 &&
            signature &&
            ((signature.num === 5 && signature.den === 8) || (signature.num === 7 && signature.den === 8))
        ) {
            unit = 1;
        }
        if (note.l <= 4 && groups[groups.length - 1].length > 0) {
            groups.push([]);
        }
        groups[groups.length - 1].push({ note, index });
        if (note.r && index < notes.length - 1) groups.push([]);
        let noteLength = (4 / note.l) * (note.d ? 1.5 : 1) * (note.t ? 2 : 3);
        timePoint += noteLength;
    });
    if (onlyGroup) return groups;
    let barAccidentals: IBarAccidental[] = [];
    let rootIndex = 0;
    groups.forEach((group, groupIndex) => {
        if (group.length > 1 && isAllPitchedOrNot(group)) {
            let totalBeamed8 = 0;
            let totalBeamed16 = 0;
            let groupLength = 0;
            group.forEach((note, index) => {
                if (note.note.p) {
                    const pitches = Array.isArray(note.note.p) ? note.note.p : [note.note.p];
                    pitches.forEach((pitch, pitchIndex) => {
                        if (pitch.a === 1 || pitch.a === -1) {
                            if (pitchPositions[rootIndex]) {
                                let alreadyHasToneIndex = barAccidentals.findIndex(
                                    a => a.tone === pitchPositions[rootIndex]![pitchIndex]
                                );
                                if (alreadyHasToneIndex !== -1) {
                                    barAccidentals.splice(alreadyHasToneIndex, 1);
                                }
                                if (pitch.a === 1) {
                                    barAccidentals.push({
                                        tone: pitchPositions[rootIndex]![pitchIndex],
                                        a: 1,
                                    });
                                } else if (pitch.a === -1) {
                                    barAccidentals.push({
                                        tone: pitchPositions[rootIndex]![pitchIndex],
                                        a: -1,
                                    });
                                }
                            }
                        }
                    });
                }

                if (index === group.length - 1) {
                    if (note.note.l < 8 || note.note.r) {
                        //|| note.note.p
                        if (totalBeamed8 < 2) {
                            totalBeamed8 = 0;
                            beaming[beaming.length - 1].beamed8 = false;
                            beaming.push({
                                beamed8: false,
                            });
                        } else {
                            totalBeamed8--;
                            beaming[beaming.length - 1].totalBeamed8 = totalBeamed8;
                            beaming.push({
                                beamed8: false,
                            });
                        }
                    } else {
                        if (totalBeamed8 > 0) {
                            beaming.push({
                                beamed8: true,
                                totalBeamed8: totalBeamed8,
                            });
                        } else {
                            beaming.push({
                                beamed8: false,
                            });
                        }
                    }
                } else {
                    if (note.note.l > 4 && !note.note.r) {
                        //&& !note.note.p
                        beaming.push({
                            beamed8: true,
                        });
                        totalBeamed8++;
                    } else {
                        if (totalBeamed8 > 1) {
                            totalBeamed8--;
                            beaming[beaming.length - 1].totalBeamed8 = totalBeamed8;
                        }
                        if (beaming.length > 0 && !beaming[beaming.length - 1].totalBeamed8) {
                            beaming[beaming.length - 1].beamed8 = false;
                        }
                        beaming.push({
                            beamed8: false,
                        });
                        totalBeamed8 = 0;
                    }
                }
                //16th
                if (index === group.length - 1) {
                    // if it is the last note of the group
                    if (note.note.l < 16 || note.note.r) {
                        //|| note.note.p
                        if (totalBeamed16 < 2) {
                            totalBeamed16 = 0;
                            beaming[beaming.length - 2].beamed16 = false;
                            beaming[beaming.length - 1].beamed16 = false;
                            if (beaming[beaming.length - 2].beamed8 && group[index - 1].note.l === 16) {
                                if (groupLength * 2 === Math.round(groupLength * 2) || note.note.r) {
                                    beaming[beaming.length - 2].soloLeft = true;
                                } else {
                                    beaming[beaming.length - 2].soloRight = true;
                                }
                                beaming[beaming.length - 2].beamed16 = true;
                            }
                        } else {
                            beaming[beaming.length - 2].totalBeamed16 = totalBeamed16 - 1;
                            beaming[beaming.length - 1].beamed16 = false;
                        }
                    } else {
                        if (totalBeamed16 > 0) {
                            beaming[beaming.length - 1].beamed16 = true;
                            beaming[beaming.length - 1].totalBeamed16 = totalBeamed16;
                        } else {
                            beaming[beaming.length - 1].beamed16 = false;
                            if (beaming[beaming.length - 1].beamed8) {
                                beaming[beaming.length - 1].soloLeft = true;
                                beaming[beaming.length - 1].beamed16 = true;
                            }
                        }
                    }
                } else {
                    // not the last of the group
                    if (note.note.l > 8 && !note.note.r) {
                        //  && !note.note.p
                        beaming[beaming.length - 1].beamed16 = true;
                        totalBeamed16++;
                    } else {
                        // TODO maybe go back at bigger groups
                        if (totalBeamed16 > 1) {
                            beaming[beaming.length - 2].totalBeamed16 = totalBeamed16 - 1;
                        } else if (index > 0 && beaming[beaming.length - 2].beamed8 && group[index - 1].note.l === 16) {
                            if (groupLength === 1 && unit === 2) {
                                beaming[beaming.length - 2].soloLeft = true;
                            } else {
                                beaming[beaming.length - 2].soloRight = true;
                            }
                            beaming[beaming.length - 2].beamed16 = true;
                        } else if (index === 1 && note.note.r) {
                            beaming[beaming.length - 2].beamed16 = false;
                        }
                        beaming[beaming.length - 1].beamed16 = false;
                        totalBeamed16 = 0;
                    }
                }
                groupLength += (4 / note.note.l) * (note.note.d === 1 ? 1.5 : 1);
                rootIndex++;
            });
        } else {
            group.forEach((note, index) => {
                beaming.push({
                    beamed8: false,
                    beamed16: false,
                });
            });
            rootIndex++;
        }
    });
    return beaming;
};

const isAllPitchedOrNot = (group: IGroup[]) => {
    let hasPitched = 0;
    let hasNotPitched = 0;
    group.forEach(el => {
        if (el.note.r) return;
        if (el.note.p) hasPitched = 1;
        else if (!el.note.p) hasNotPitched = 1;
    });
    if (hasPitched + hasNotPitched === 1) return true;
    return false;
};

export default parseBeams;
